// Musical frequencies (in Hz) for a friendly "pop" sound
const NOTE_C5 = 523.25;
const NOTE_E5 = 659.25;

// Anime mode: higher-pitched sparkly chord
const NOTE_E6 = 1318.51;
const NOTE_GS6 = 1661.22;
const NOTE_B6 = 1975.53;

// Audio timing constants (in seconds)
const NOTE_DELAY = 0.04; // Delay between consecutive notes
const ATTACK_TIME = 0.02; // Time to reach peak volume
const MAX_VOLUME = 0.12; // Peak gain level (0-1)
const MIN_VOLUME = 0.001; // Minimum gain before stopping
const NOTE_DURATION = 0.15; // Duration of each note

// Anime sound constants
const ANIME_NOTE_DELAY = 0.03; // Delay between anime chord notes
const ANIME_MAX_VOLUME = 0.08; // Peak gain for anime sparkle
const ANIME_ATTACK_TIME = 0.01; // Attack time for anime notes
const ANIME_NOTE_DURATION = 0.2; // Duration of anime sparkle notes

const SOUND_STORAGE_KEY = 'soundEnabled';

let audioContext: AudioContext | null = null;
// Cache the sound enabled state to avoid localStorage reads on every click
let cachedSoundEnabled: boolean | null = null;

const getAudioContext = (): AudioContext => {
	if (!audioContext) {
		audioContext = new AudioContext();
	}

	return audioContext;
};

export const isSoundEnabled = (): boolean => {
	// Return cached value if available, otherwise read from localStorage
	if (cachedSoundEnabled === null) {
		const stored = localStorage.getItem(SOUND_STORAGE_KEY);
		cachedSoundEnabled = stored !== 'false';
	}

	return cachedSoundEnabled;
};

export const setSoundEnabled = (enabled: boolean): void => {
	localStorage.setItem(SOUND_STORAGE_KEY, String(enabled));
	cachedSoundEnabled = enabled; // Update cache when setting
};

let animeModeEnabled = false;

export const setAnimeSoundMode = (enabled: boolean): void => {
	animeModeEnabled = enabled;
};

const playAnimeSound = (): void => {
	try {
		const ctx = getAudioContext();
		const now = ctx.currentTime;
		const frequencies = [NOTE_E6, NOTE_GS6, NOTE_B6];

		frequencies.forEach((freq, i) => {
			const oscillator = ctx.createOscillator();
			const gainNode = ctx.createGain();

			oscillator.connect(gainNode);
			gainNode.connect(ctx.destination);

			oscillator.type = 'triangle';
			oscillator.frequency.setValueAtTime(freq, now);

			const startTime = now + i * ANIME_NOTE_DELAY;
			gainNode.gain.setValueAtTime(0, startTime);
			gainNode.gain.linearRampToValueAtTime(ANIME_MAX_VOLUME, startTime + ANIME_ATTACK_TIME);
			gainNode.gain.exponentialRampToValueAtTime(MIN_VOLUME, startTime + ANIME_NOTE_DURATION);

			oscillator.start(startTime);
			oscillator.stop(startTime + ANIME_NOTE_DURATION);
		});
	} catch {
		// Silently fail
	}
};

export const playCopySound = (): void => {
	// Use cached value directly without localStorage read
	if (cachedSoundEnabled === false) {
		return;
	}
	// Double-check cache in case it hasn't been initialized
	if (cachedSoundEnabled === null && !isSoundEnabled()) {
		return;
	}
	if (animeModeEnabled) {
		playAnimeSound();

		return;
	}
	try {
		const ctx = getAudioContext();
		const now = ctx.currentTime;

		// Create two soft tones for a friendly "pop" sound
		const frequencies = [NOTE_C5, NOTE_E5]; // C5 and E5 - major third interval

		frequencies.forEach((freq, i) => {
			const oscillator = ctx.createOscillator();
			const gainNode = ctx.createGain();

			oscillator.connect(gainNode);
			gainNode.connect(ctx.destination);

			oscillator.type = 'sine';
			oscillator.frequency.setValueAtTime(freq, now);

			const startTime = now + i * NOTE_DELAY;
			gainNode.gain.setValueAtTime(0, startTime);
			gainNode.gain.linearRampToValueAtTime(MAX_VOLUME, startTime + ATTACK_TIME);
			gainNode.gain.exponentialRampToValueAtTime(MIN_VOLUME, startTime + NOTE_DURATION);

			oscillator.start(startTime);
			oscillator.stop(startTime + NOTE_DURATION);
		});
	} catch {
		// Silently fail if audio is not supported
	}
};
